import {useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Crown, Gift, Pencil, Trash2, UserPlus, Users} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {createIdempotencyRecord, refreshIdempotencyRecord} from "../../shared/api/idempotency";
import {AdminTable} from "../../shared/components/AdminTable";
import {ConfirmationDialog} from "../../shared/components/ConfirmationDialog";
import {DataState} from "../../shared/components/DataState";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput} from "../../shared/components/PageToolbar";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {formatNumber} from "../../shared/utils/format";
import {useActiveAdminCountryScope} from "../organizations/useActiveAdminCountryScope";
import {
    changeClubMemberPart,
    createClubMember,
    deleteClubMember,
    fillAllClubMemberBonuses,
    fillClubMemberBonus,
    loadClubMembers,
} from "./api";
import {
    clubMemberLabel,
    normalizeClubAmount,
    normalizePhoneNumber,
    normalizePlacesCount,
    pageFromPaginationLink,
} from "./contracts";
import {useTranslation, i18n} from "../../shared/i18n";


const EMPTY_DIALOG = Object.freeze({type: null, member: null, amount: null});

export const ClubPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const {countryScope, countryId, activeCountryScopeRef} = useActiveAdminCountryScope(capabilities);
    const [draftSearch, setDraftSearch] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [dialog, setDialog] = useState(EMPTY_DIALOG);
    const [dialogError, setDialogError] = useState(null);
    const [result, setResult] = useState(null);
    const idempotencyRecords = useRef({});

    useEffect(() => {
        setPage(1);
        setDialog(EMPTY_DIALOG);
        setDialogError(null);
        setResult(null);
    }, [countryScope]);

    const membersQuery = useQuery({
        queryKey: ["admin-club-members", countryScope, page, search, pageSize],
        queryFn: () => loadClubMembers({countryId, page, search, pageSize}),
        retry: false,
    });
    const rows = membersQuery.data?.results || [];

    const mutation = useMutation({
        mutationFn: async (command) => {
            if (command.type === "create") return createClubMember(command);
            if (command.type === "part") return changeClubMemberPart(command);
            if (command.type === "fill") return fillClubMemberBonus(command);
            if (command.type === "fill-all") return fillAllClubMemberBonuses(command);
            if (command.type === "delete") return deleteClubMember(command);
            throw new Error("unsupported_club_command");
        },
        onSuccess: (_response, variables) => {
            if (activeCountryScopeRef.current !== variables.countryScope) return;
            delete idempotencyRecords.current[variables.type];
            setDialog(EMPTY_DIALOG);
            setDialogError(null);
            setResult({kind: "success", message: variables.successMessage});
            void queryClient.invalidateQueries({queryKey: ["admin-club-members", variables.countryScope]});
        },
        onError: (error, variables) => {
            if (activeCountryScopeRef.current !== variables.countryScope) return;
            const mapped = mapApiError(error);
            setDialogError(mapped);
            setResult({kind: "error", message: mapped.message});
        },
    });

    const openDialog = (type, member = null) => {
        setDialog({type, member, amount: null});
        setDialogError(null);
        setResult(null);
    };

    const closeDialog = () => {
        if (mutation.isPending) return;
        setDialog(EMPTY_DIALOG);
        setDialogError(null);
    };

    const idempotencyKey = (type, fingerprint) => {
        const current = idempotencyRecords.current[type];
        const record = current
            ? refreshIdempotencyRecord(current, fingerprint, `club-${type}`)
            : createIdempotencyRecord(`club-${type}`, fingerprint);
        idempotencyRecords.current[type] = record;
        return record.key;
    };

    const mutate = (command) => {
        if (!countryId) {
            setDialogError({message: t("sections.club.p1")});
            return;
        }
        mutation.mutate({...command, countryId, countryScope});
    };

    const submitForm = (values) => {
        try {
            if (dialog.type === "create") {
                const phoneNumber = normalizePhoneNumber(values.phone_number);
                if (!phoneNumber) throw new Error(t("sections.club.p2"));
                mutate({
                    type: "create",
                    phoneNumber,
                    placesCount: normalizePlacesCount(values.places_count),
                    successMessage: t("sections.club.p3"),
                });
                return;
            }
            if (dialog.type === "part") {
                mutate({
                    type: "part",
                    userId: dialog.member.id,
                    placesCount: normalizePlacesCount(values.places_count),
                    successMessage: t("sections.club.p4"),
                });
                return;
            }
            if (dialog.type === "fill") {
                const amount = normalizeClubAmount(values.amount);
                mutate({
                    type: "fill",
                    userId: dialog.member.id,
                    amount,
                    idempotencyKey: idempotencyKey("fill", `${dialog.member.id}:${amount}:${countryId}`),
                    successMessage: t("sections.club.p5", {v0: clubMemberLabel(dialog.member), v1: amount}),
                });
                return;
            }
            if (dialog.type === "fill-all") {
                setDialog({type: "fill-all-confirm", member: null, amount: normalizeClubAmount(values.amount)});
                setDialogError(null);
            }
        } catch (error) {
            setDialogError({message: error.message});
        }
    };

    const confirm = () => {
        if (dialog.type === "delete") {
            mutate({
                type: "delete",
                userId: dialog.member.id,
                successMessage: t("sections.club.p6", {v0: clubMemberLabel(dialog.member)}),
            });
            return;
        }
        if (dialog.type === "fill-all-confirm") {
            const amount = dialog.amount;
            mutate({
                type: "fill-all",
                amount,
                idempotencyKey: idempotencyKey("fill-all", `${amount}:${countryId}`),
                successMessage: t("sections.club.p7", {v0: amount}),
            });
        }
    };

    const columns = useMemo(() => [
        {key: "id", label: "ID"},
        {key: "name", label: t("sections.club.p8"), render: (row) => clubMemberLabel(row)},
        {key: "phone_number", label: t("sections.club.p9"), render: (row) => row.phone_number || "—"},
        {key: "club_part", label: t("sections.club.p10"), render: (row) => formatNumber(row.club_part)},
        {
            key: "actions",
            label: t("sections.club.p11"),
            render: (row) => (
                <div className="admin-inline-actions">
                    <button className="admin-icon-button" type="button" title={i18n.t("sections.club.p12")} aria-label={i18n.t("sections.club.p13", {v0: clubMemberLabel(row)})} onClick={() => openDialog("fill", row)}>
                        <Gift size={16} />
                    </button>
                    <button className="admin-icon-button" type="button" title={i18n.t("sections.club.p14")} aria-label={i18n.t("sections.club.p15", {v0: clubMemberLabel(row)})} onClick={() => openDialog("part", row)}>
                        <Pencil size={16} />
                    </button>
                    <button className="admin-icon-button is-danger" type="button" title={i18n.t("sections.club.p16")} aria-label={i18n.t("sections.club.p17", {v0: clubMemberLabel(row)})} onClick={() => openDialog("delete", row)}>
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
        },
    ], []);

    const formDialog = ["create", "part", "fill", "fill-all"].includes(dialog.type);
    const formConfig = {
        create: {
            title: t("sections.club.p18"),
            entity: "Club",
            description: t("sections.club.p19"),
            fields: [
                {name: "phone_number", label: t("sections.club.p9"), required: true},
                {name: "places_count", label: t("sections.club.p10"), type: "number", required: true},
            ],
            confirmLabel: t("sections.club.p20"),
        },
        part: {
            title: t("sections.club.p21"),
            entity: clubMemberLabel(dialog.member),
            details: [{label: t("sections.club.p22"), value: formatNumber(dialog.member?.club_part)}],
            fields: [{name: "places_count", label: t("sections.club.p23"), type: "number", required: true}],
            confirmLabel: t("sections.club.p24"),
        },
        fill: {
            title: t("sections.club.p25"),
            entity: clubMemberLabel(dialog.member),
            description: t("sections.club.p26"),
            fields: [{name: "amount", label: t("sections.club.p27"), required: true}],
            confirmLabel: t("sections.club.p12"),
        },
        "fill-all": {
            title: t("sections.club.p28"),
            entity: t("sections.club.p29", {v0: formatNumber(membersQuery.data?.count)}),
            description: t("sections.club.p30"),
            fields: [{name: "amount", label: t("sections.club.p31"), required: true}],
            confirmLabel: t("sections.club.p32"),
        },
    }[dialog.type] || {};

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("sections.club.p33")}</h1>
                    <p className="admin-page-copy">{t("sections.club.p34")}</p>
                </div>
                <div className="admin-pill"><Crown size={14} /> {formatNumber(membersQuery.data?.count)} {t("sections.club.p35")}</div>
            </header>

            <section className="admin-grid cols-3">
                <article className="admin-card">
                    <p className="admin-card-title"><Users size={14} /> {t("sections.club.p36")}</p>
                    <p className="admin-card-value">{formatNumber(membersQuery.data?.count)}</p>
                    <p className="admin-card-note">{t("sections.club.p37")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.club.p38")}</p>
                    <p className="admin-card-value">Ledger</p>
                    <p className="admin-card-note">{t("sections.club.p39")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.club.p40")}</p>
                    <p className="admin-card-value">{countryScope === "0" ? t("sections.club.p41") : countryScope || "—"}</p>
                    <p className="admin-card-note">{t("sections.club.p42")}</p>
                </article>
            </section>

            <MutationResultBanner result={result} />
            {!countryId && (
                <DataState title={t("sections.club.p43")} copy={t("sections.club.p44")} />
            )}
            <PageToolbar>
                <ToolbarInput label={t("sections.club.p45")} value={draftSearch} placeholder={t("sections.club.p46")} onChange={(event) => setDraftSearch(event.target.value)} />
                <ToolbarButton onClick={() => { setPage(1); setSearch(draftSearch.trim()); }}>{t("sections.club.p47")}</ToolbarButton>
                <PageSizeSelect value={pageSize} options={[20, 50, 100]} onChange={(size) => { setPage(1); setPageSize(size); }} />
                <ToolbarButton variant="secondary" disabled={!countryId} onClick={() => openDialog("create")}>
                    <UserPlus size={15} /> {t("sections.club.p20")}
                </ToolbarButton>
                <ToolbarButton variant="secondary" disabled={!countryId || !rows.length} onClick={() => openDialog("fill-all")}>
                    <Gift size={15} /> {t("sections.club.p48")}
                </ToolbarButton>
            </PageToolbar>

            {membersQuery.isError ? (
                <DataState title={t("sections.club.p49")} copy={mapApiError(membersQuery.error).message} />
            ) : (
                <>
                    <AdminTable columns={columns} rows={rows} loading={membersQuery.isLoading} emptyText={t("sections.club.p50")} />
                    <ServerPagination
                        count={membersQuery.data?.count}
                        page={page}
                        pageSize={pageSize}
                        previous={membersQuery.data?.previous}
                        next={membersQuery.data?.next}
                        pending={membersQuery.isFetching}
                        onPageChange={(link) => setPage((current) => pageFromPaginationLink(link, current))}
                    />
                </>
            )}

            <FinancialCommandDialog
                open={formDialog}
                dialogKey={`${dialog.type}:${dialog.member?.id || "all"}`}
                pending={mutation.isPending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={submitForm}
                {...formConfig}
            />
            <ConfirmationDialog
                open={dialog.type === "delete" || dialog.type === "fill-all-confirm"}
                dialogKey={`${dialog.type}:${dialog.member?.id || dialog.amount || ""}`}
                title={dialog.type === "delete" ? t("sections.club.p51") : t("sections.club.p52")}
                entity={dialog.type === "delete" ? clubMemberLabel(dialog.member) : t("sections.club.p53", {v0: dialog.amount || ""})}
                description={dialog.type === "delete" ? t("sections.club.p54") : t("sections.club.p55")}
                confirmLabel={dialog.type === "delete" ? t("sections.club.p16") : t("sections.club.p48")}
                confirmationText={dialog.type === "fill-all-confirm" ? t("sections.club.p56") : undefined}
                tone="destructive"
                pending={mutation.isPending}
                error={dialogError}
                onCancel={closeDialog}
                onConfirm={confirm}
            />
        </div>
    );
};
